// Copyright (c) MobiFactory. All rights reserved.
// This source code is the property of MobiFactory. It is exclusively available to customers who have a valid user license.
// See LICENSE.txt file in the project root for full license information.

import {
  CountryCode,
  findPhoneNumbersInText,
  NumberFound,
  parsePhoneNumberFromString,
  PhoneNumber,
  NumberType,
} from "libphonenumber-js/max";

const DEFAULT_COUNTRY_CODE = "FR" as CountryCode;
const MOBILE_NUMBER_TYPE = "MOBILE" as NumberType;

/**
 * Get default country code based on the navigator locale
 * @returns {string} Country code
 */
const getCountryCode = function (): CountryCode {
  const regex = /\w\w-(\w\w)/;
  const language = Office.context.displayLanguage;
  if (regex.test(language)) {
    const country = regex.exec(language);
    if (!country) {
      return DEFAULT_COUNTRY_CODE;
    }
    return country[1] as CountryCode;
  }

  return DEFAULT_COUNTRY_CODE;
};

/**
 * Helper class for phone numbers
 */
export default class PhoneHelper {
  /**
   * Formats phone number
   * @param {string} value - raw phone value
   * @returns {string} Formatted number
   */
  static format(value: string): string {
    if (value) {
      const localCountryCode = getCountryCode();
      const phoneNumber = parsePhoneNumberFromString(value, localCountryCode);
      if (phoneNumber) {
        return localCountryCode === phoneNumber.country
          ? phoneNumber.formatNational()
          : phoneNumber.formatInternational();
      }
    }
    return value;
  }

  /**
   * Parse a string to get a number
   * @param {string} value - phone value
   * @returns {string} E.164 formatted phone number
   */
  static parse(value: string): string {
    if (value) {
      const phoneNumber = parsePhoneNumberFromString(value, getCountryCode());
      if (phoneNumber) {
        return phoneNumber.number.toString();
      }
    }
    return value;
  }

  /**
   * test whether the string is a valid phone number
   * @param {string} value - value to test
   * @returns {boolean} True if valid
   */
  static validate(value: string): boolean {
    if (value) {
      const phoneNumber = parsePhoneNumberFromString(value, getCountryCode());
      if (!phoneNumber) {
        return false;
      }
      return phoneNumber.isValid();
    }
    return false;
  }

  /**
   * Gets phone numbers from text
   * @param text
   */
  static getPhoneNumbersFromText(text: string): NumberFound[] {
    return findPhoneNumbersInText(text, getCountryCode());
  }

  /**
   * Gets a value indicating whether it is a mobile phone number
   * @param phoneNumber
   */
  static isPhoneNumberMobile(phoneNumber: PhoneNumber): boolean {
    return phoneNumber.getType() == MOBILE_NUMBER_TYPE;
  }

  /**
   * Parse a string to get a phone number
   * @param {string} value - phone value
   * @returns {string} E.164 formatted phone number
   */
  static parseFromString(value: string): PhoneNumber | null {
    if (value) {
      const phoneNumber = parsePhoneNumberFromString(value, getCountryCode());
      if (phoneNumber) {
        return phoneNumber;
      }
    }
    return null;
  }
}
